import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic2, Square } from "lucide-react";

/**
 * RecordingStopBanner — floating, unmissable Stop-Recording banner pinned
 * to the top of the viewport whenever a recording is in progress. Solves
 * the iter-148 UX bug where the tiny per-row record dot was hard to find
 * once the user was deep in the mixer scroll.
 *
 * Visible when `recordingIdx !== null` (single-channel record) OR `tracking`
 * is true (multi-track tracking session).
 *
 * Three ways to stop:
 *   1. Click the banner anywhere.
 *   2. Press Spacebar.
 *   3. Press Escape.
 *
 * Spacebar normally toggles play, but during a take we want it to STOP
 * the recording instead — the user almost never wants to start playback
 * during a take. Escape is a universal "abort current modal action" cue.
 */
export default function RecordingStopBanner({ recordingIdx, tracking, onStop }) {
  const [elapsed, setElapsed] = useState(0);
  const isRecording = recordingIdx !== null || tracking;

  // Tick a once-per-second counter while recording. Cleared on stop so the
  // banner starts at 00:00 on the next take.
  useEffect(() => {
    if (!isRecording) { setElapsed(0); return; }
    const start = Date.now();
    setElapsed(0);
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 250);
    return () => clearInterval(id);
  }, [isRecording]);

  // Keyboard shortcuts. Skip if the focused element is an input/textarea so
  // typing the letter S or hitting space in a name field doesn't kill takes.
  useEffect(() => {
    if (!isRecording) return;
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.target?.isContentEditable) return;
      if (e.code === "Space" || e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onStop?.();
      }
    };
    window.addEventListener("keydown", handler, true); // capture so we beat other handlers
    return () => window.removeEventListener("keydown", handler, true);
  }, [isRecording, onStop]);

  if (!isRecording) return null;

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const channelLabel = recordingIdx !== null
    ? `CH ${String(recordingIdx + 1).padStart(2, "0")}`
    : "Multi-track";

  return (
    <AnimatePresence>
      <motion.button
        key="recording-stop-banner"
        data-testid="recording-stop-banner"
        type="button"
        onClick={onStop}
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -60, opacity: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
        className="fixed left-1/2 top-3 z-[100] -translate-x-1/2 cursor-pointer rounded-2xl border border-rose-300/60 bg-gradient-to-r from-rose-600 via-rose-500 to-rose-600 px-5 py-2.5 text-white shadow-[0_8px_32px_-8px_rgba(244,63,94,0.6),0_0_0_1px_rgba(244,63,94,0.2)] backdrop-blur"
      >
        <span className="flex items-center gap-3">
          {/* Pulsing red dot */}
          <span className="relative grid h-3 w-3 place-items-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-200/85" />
            <span className="relative h-3 w-3 rounded-full bg-rose-200" />
          </span>

          <Mic2 className="h-4 w-4" />

          <span className="flex flex-col items-start leading-tight">
            <span className="font-display text-[14px] font-bold tracking-wide">
              RECORDING · {channelLabel}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-rose-100/85">
              {mm}:{ss} · click here or press Space / Esc to stop
            </span>
          </span>

          <span className="ml-1 grid h-7 w-7 place-items-center rounded-md bg-white/15 ring-1 ring-white/20">
            <Square className="h-3.5 w-3.5" fill="currentColor" />
          </span>
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
